import React from 'react'
import { PageLayout } from '../components/common/PageLayout'
import { Section } from '../components/common/DesignComponents'

export function TermsPage(): React.ReactElement {
  const lastUpdated = 'January 2024'

  const terms = [
    {
      title: 'Acceptance of Terms',
      content: 'By accessing and using BabyKingdom, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.'
    },
    {
      title: 'Use License',
      content: 'Permission is granted to temporarily download one copy of the materials (information or software) on BabyKingdom for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:'
    },
    {
      title: 'License Restrictions',
      items: [
        'Modify or copy the materials',
        'Use the materials for any commercial purpose or for any public display',
        'Attempt to decompile or reverse engineer any software contained on BabyKingdom',
        'Remove any copyright or other proprietary notations from the materials',
        'Transfer the materials to another person or "mirror" the materials on any other server',
        'Use BabyKingdom or its content to harm children or engage in illegal activity'
      ]
    },
    {
      title: 'Age and Parental Consent',
      content: 'BabyKingdom is intended for children ages 3-12 with parental or guardian supervision. Parents and guardians are responsible for monitoring their child\'s use of the service. By creating an account, you confirm that you are the parent or legal guardian and consent to the terms of this agreement on behalf of your child.'
    },
    {
      title: 'User Accounts',
      items: [
        'You are responsible for maintaining the confidentiality of your account information and password',
        'You agree to accept responsibility for all activities that occur under your account',
        'You agree to provide accurate, current, and complete information during registration',
        'You are responsible for ensuring your child does not use inappropriate language or behavior',
        'BabyKingdom reserves the right to suspend or terminate accounts that violate our policies'
      ]
    },
    {
      title: 'User Content',
      content: 'Any content you submit to BabyKingdom (including child profile names, messages, or artwork) must be original, non-infringing, and appropriate for a family-friendly platform. By submitting content, you grant BabyKingdom a license to use, modify, and display that content. You retain ownership of your content and may request deletion at any time.'
    },
    {
      title: 'In-App Purchases',
      items: [
        'Premium subscriptions renew automatically on a monthly basis',
        'All sales are final and non-refundable except where required by law',
        'You may cancel your subscription at any time through your account settings',
        'Coins and in-game items are virtual goods and do not represent actual currency',
        'BabyKingdom reserves the right to adjust coin values or item availability'
      ]
    },
    {
      title: 'Prohibited Conduct',
      content: 'You agree not to:'
    },
    {
      title: 'Prohibited Activities',
      items: [
        'Harass, abuse, or harm other users',
        'Access the service illegally or use it for illegal purposes',
        'Upload or transmit viruses or other harmful code',
        'Attempt to access other users\' accounts or data',
        'Spam or send unsolicited messages',
        'Impersonate another person or entity',
        'Use automated tools to access the service without permission'
      ]
    },
    {
      title: 'Intellectual Property Rights',
      content: 'All content on BabyKingdom, including text, graphics, logos, images, and software, is the property of BabyKingdom or its content suppliers and is protected by international copyright laws. You may not reproduce, distribute, or transmit any content without our prior written permission.'
    },
    {
      title: 'Limitation of Liability',
      content: 'BabyKingdom and its suppliers shall not be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on BabyKingdom.'
    },
    {
      title: 'Disclaimer of Warranties',
      content: 'The materials on BabyKingdom are provided on an \'as is\' basis. BabyKingdom makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.'
    },
    {
      title: 'Third-Party Services',
      content: 'BabyKingdom may contain links to third-party websites and services. We are not responsible for the availability or content of these external sites. Your use of third-party services is subject to their terms and policies.'
    },
    {
      title: 'Modifications to Terms',
      content: 'BabyKingdom may revise these terms of service at any time without notice. By using this service, you are agreeing to be bound by the then current version of these terms of service. We will notify you of significant changes via email.'
    },
    {
      title: 'Governing Law',
      content: 'These terms and conditions are governed by and construed in accordance with the laws of the United States, and you irrevocably submit to the exclusive jurisdiction of the courts located in this location.'
    },
    {
      title: 'Contact Information',
      content: 'If you have any questions about these Terms of Service, please contact us at legal@babykingdom.com or by mail at: BabyKingdom Legal Team, 123 Learning Lane, Education City, EC 12345'
    }
  ]

  return (
    <PageLayout title="Terms of Service" subtitle="Please read our terms carefully">
      <Section title="">
        <div className="mb-8 p-6 bg-yellow-50 border-4 border-yellow-200 rounded-2xl">
          <p className="text-gray-700 font-bold text-lg">
            ⚖️ These Terms of Service govern your use of BabyKingdom. By using our service, you agree to these terms.
          </p>
          <p className="text-gray-700 font-bold mt-3">
            <strong>Last Updated:</strong> {lastUpdated}
          </p>
        </div>

        <div className="space-y-8">
          {terms.map((term, idx) => (
            <div key={idx} className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-4 border-purple-200">
              <h3 className="text-2xl font-black text-purple-700 mb-4">{idx + 1}. {term.title}</h3>

              {term.content && (
                <p className="text-gray-700 font-semibold mb-4">{term.content}</p>
              )}

              {term.items && (
                <ul className="space-y-3">
                  {term.items.map((item, iIdx) => (
                    <li key={iIdx} className="flex gap-3 items-start">
                      <span className="font-black text-purple-500 text-xl mt-0 flex-shrink-0">▪</span>
                      <span className="text-gray-700 font-semibold">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 p-8 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl border-4 border-green-300">
          <h4 className="text-2xl font-black text-green-700 mb-4">🎯 Key Takeaways</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">👨‍👩‍👧</span>
              <p className="text-gray-700 font-bold">Parents are responsible for supervising children's use</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🚫</span>
              <p className="text-gray-700 font-bold">Inappropriate content or behavior will result in account suspension</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">💳</span>
              <p className="text-gray-700 font-bold">In-app purchases are final and subscriptions auto-renew</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">©️</span>
              <p className="text-gray-700 font-bold">All content is protected by intellectual property laws</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center p-6 bg-purple-100 rounded-2xl border-4 border-purple-300">
          <p className="text-gray-700 font-bold">
            By using BabyKingdom, you agree to these terms and our Privacy Policy.
          </p>
        </div>
      </Section>
    </PageLayout>
  )
}
